<template>
  <div class="sidebar" :class="isOpened ? 'open' : ''">
    <!-- Logo area -->
    <div class="sidebar-logo">
      <div class="logo-img" :class="{ 'logo-visible': isOpened }">
        <img :src="user.userLogo" />
      </div>
      <button class="sidebar-toggle" @click="isOpened = !isOpened">
        <i class="bx" :class="isOpened ? 'bx-chevrons-left' : 'bx-menu'" />
      </button>
    </div>

    <div v-show="user.isAuthenticated" class="sidebar-body">
      <!-- Navigation -->
      <nav class="sidebar-nav" id="sidebar-scroll">
        <ul class="nav-items">
          <li v-if="isSearch" @click="isOpened = true" class="nav-search-item">
            <i class="bx bx-search nav-icon" />
            <input type="text" :placeholder="searchPlaceholder"
              @input="$emit('search-input-emit', $event.target.value)">
            <span class="nav-tooltip">{{ searchTooltip }}</span>
          </li>

          <template v-for="(menuItem, index) in menuItems" :key="index">
            <li v-if="user.userRoleId <= menuItem.roleId && menuItem.departmentId.includes(user.userDepartmentId)">
              <router-link :to="menuItem.link" class="nav-link">
                <div class="nav-icon-wrap">
                  <i class="bx nav-icon" :class="menuItem.icon || 'bx-square-rounded'" />
                </div>
                <span class="nav-label">{{ $t('menu.' + menuItem.i18n + '.menuitem') }}</span>
              </router-link>
              <span class="nav-tooltip">{{ menuItem.tooltip || menuItem.name }}</span>
            </li>
          </template>
        </ul>
      </nav>

      <!-- Footer controls -->
      <div class="sidebar-footer">
        <div class="locale-switcher" :class="{ 'locale-expanded': isOpened }">
          <i class="bx bx-globe locale-icon"></i>
          <span v-show="isOpened" class="locale-label">{{ $t('i18n.chooseLang') }}</span>
          <select v-model="$i18n.locale" class="locale-select">
            <option v-for="locale in $i18n.availableLocales" :key="`locale-${locale}`" :value="locale">{{ locale }}</option>
          </select>
        </div>
        <button class="logout-btn" v-show="user.isAuthenticated" @click="handleLogout()">
          <i class="bx bx-log-out-circle logout-icon"></i>
          <span v-show="isOpened" class="logout-label">{{ $t('i18n.login.logout') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useSettings } from '@/store/user';

export default {
  name: 'SidebarMenuAkahon',
  setup() {
    return {
      user: useSettings()
    }
  },
  props: {
    //! Menu settings
    isMenuOpen: {
      type: Boolean,
      default: true,
    },
    menuTitle: {
      type: String,
      default: 'Akahon',
    },
    menuLogo: {
      type: String,
      default: '',
    },
    menuIcon: {
      type: String,
      default: 'bxl-c-plus-plus',
    },
    isPaddingLeft: {
      type: Boolean,
      default: true,
    },
    menuOpenedPaddingLeftBody: {
      type: String,
      default: '240px'
    },
    menuClosedPaddingLeftBody: {
      type: String,
      default: '64px'
    },
    //! Menu items
    menuItems: {
      type: Array,
      default: () => [
        {
          link: '/',
          name: 'Overblik',
          tooltip: 'Overblik',
          icon: 'bx-grid-alt',
          i18n: 'overview',
          roleId: '10',
          departmentId: [1,2,3]
        },
        {
          link: '/store-locator',
          name: 'Store Locator',
          tooltip: 'Store Locator',
          icon: 'bx-map-pin',
          i18n: 'storeLocator',
          roleId: '10',
          departmentId: [1,2,3]
        },
        {
          link: '/translations',
          name: 'Translations',
          tooltip: 'Translations',
          icon: 'bx-globe-alt',
          i18n: 'translations',
          roleId: '3',
          departmentId: [1,4]
        },
        {
          link: '/pim',
          name: 'PIM',
          tooltip: 'PIM',
          icon: 'bx-package',
          i18n: 'pim',
          roleId: '2',
          departmentId: [1]
        },
        {
          link: '/users',
          name: 'Brugere',
          tooltip: 'Brugere',
          icon: 'bx-group',
          i18n: 'users',
          roleId: '2',
          departmentId: [1,2,3,4]
        },
        {
          link: '/profile',
          name: 'Min bruger',
          tooltip: 'Min bruger',
          icon: 'bx-user',
          i18n: 'user_profile',
          roleId: '10',
          departmentId: [1,2,3,4]
        },
        {
          link: '/settings',
          name: 'Settings',
          tooltip: 'Settings',
          icon: 'bx-cog',
          i18n: 'admin_settings',
          roleId: '2',
          departmentId: [1]
        },
      ],
    },
    //! Search
    isSearch: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...',
    },
    searchTooltip: {
      type: String,
      default: 'Search',
    },
    //! Profile details
    profileImg: {
      type: String,
      default: '',
    },
    profileName: {
      type: String,
      default: '',
    },
    profileRole: {
      type: String,
      default: '',
    },
    isExitButton: {
      type: Boolean,
      default: true,
    },
    isLoggedIn: {
      type: Boolean,
      default: true,
    },
    //! Styles
    bgColor: {
      type: String,
      default: '#e6e6e6',
    },
    secondaryColor: {
      type: String,
      default: '#e6e6e6',
    },
    homeSectionColor: {
      type: String,
      default: '#e6e6e6',
    },
    logoTitleColor: {
      type: String,
      default: '#585858',
    },
    iconsColor: {
      type: String,
      default: '#585858',
    },
    itemsTooltipColor: {
      type: String,
      default: '#e6e6e6',
    },
    searchInputTextColor: {
      type: String,
      default: '#585858',
    },
    menuItemsHoverColor: {
      type: String,
      default: '#d3d3d3',
    },
    menuItemsTextColor: {
      type: String,
      default: '#585858',
    },
    menuFooterTextColor: {
      type: String,
      default: '#585858',
    },
  },
  data() {
    return {
      isOpened: false,
    }
  },
  mounted() {
    this.isOpened = this.isMenuOpen
  },
  watch: {
    isOpened() {
      window.document.body.style.paddingLeft = this.isOpened && this.isPaddingLeft ? this.menuOpenedPaddingLeftBody : this.menuClosedPaddingLeftBody
    }
  },
  methods: {
    handleLogout() {
      this.user.userLogout();
    },
  },
}
</script>

<style>
/* Google Font Link */
@import url('https://unpkg.com/boxicons@2.0.7/css/boxicons.min.css');

/* === Sidebar Shell === */
.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 64px;
  background: #0b0f13;
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  z-index: 99;
  display: flex;
  flex-direction: column;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.sidebar.open {
  width: 240px;
}

/* === Logo === */
.sidebar-logo {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}
.logo-img {
  opacity: 0;
  width: 0;
  transition: all 0.3s ease;
  overflow: hidden;
}
.logo-img.logo-visible {
  opacity: 1;
  width: 120px;
}
.logo-img img {
  width: 120px;
  object-fit: contain;
}
.sidebar-toggle {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #9aa0a8;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}
.sidebar-toggle:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #e8eaed;
}

/* === Body === */
.sidebar-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

/* === Navigation === */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem 0.5rem;
}
#sidebar-scroll::-webkit-scrollbar {
  display: none;
}
.nav-items {
  list-style: none;
  margin: 0;
  padding: 0;
}
.nav-items li {
  position: relative;
  margin-bottom: 2px;
}

/* === Nav Links === */
.nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0.5rem;
  height: 42px;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
  position: relative;
}
.nav-link:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* Active state */
a.router-link-active.router-link-exact-active.nav-link {
  background: rgba(117, 35, 35, 0.08);
}
a.router-link-active.router-link-exact-active.nav-link::before {
  content: '';
  position: absolute;
  left: -0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 0 3px 3px 0;
  background: #a63c3c;
}
a.router-link-active.router-link-exact-active.nav-link .nav-icon {
  color: #a63c3c;
}
a.router-link-active.router-link-exact-active.nav-link .nav-label {
  color: #e8eaed;
}

/* Icon wrapper */
.nav-icon-wrap {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nav-icon {
  font-size: 20px;
  color: #5c6370;
  transition: color 0.2s ease;
  line-height: 1;
  /* Reset any inherited sizing */
  height: auto;
  min-width: auto;
}
.nav-link:hover .nav-icon {
  color: #9aa0a8;
}

/* Label */
.nav-label {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #9aa0a8;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
}
.sidebar.open .nav-label {
  opacity: 1;
  pointer-events: auto;
}

/* Tooltips */
.nav-tooltip {
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: #232b35;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e8eaed;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  z-index: 100;
}
.nav-items li:hover .nav-tooltip {
  opacity: 1;
}
.sidebar.open .nav-tooltip {
  display: none;
}

/* === Footer === */
.sidebar-footer {
  padding: 0.75rem 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  flex-shrink: 0;
}

.locale-switcher {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  margin-bottom: 0.25rem;
}
.locale-icon {
  font-size: 18px;
  color: #5c6370;
  flex-shrink: 0;
}
.locale-label {
  font-size: 0.75rem;
  color: #5c6370;
  white-space: nowrap;
}
.locale-select {
  background: #1a2028;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #9aa0a8;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 0.75rem;
  padding: 0.25rem 0.35rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s ease;
}
.locale-select:hover,
.locale-select:focus {
  border-color: rgba(117, 35, 35, 0.3);
}
.locale-select option {
  background: #1a2028;
  color: #e8eaed;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}
.logout-btn:hover {
  background: rgba(237, 106, 90, 0.08);
}
.logout-icon {
  font-size: 20px;
  color: #5c6370;
  transition: color 0.2s ease;
  /* Reset inherited icon styling */
  height: auto;
  min-width: auto;
  line-height: 1;
}
.logout-btn:hover .logout-icon {
  color: #ED6A5A;
}
.logout-label {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 0.8125rem;
  color: #9aa0a8;
  white-space: nowrap;
}
.logout-btn:hover .logout-label {
  color: #ED6A5A;
}

/* Reset the global i tag styling from old sidebar */
.sidebar i {
  height: auto;
  min-width: auto;
  font-size: inherit;
  text-align: center;
  line-height: 1;
}

/* === Old layout compat === */
.home-section {
  position: relative;
  background: #0f1419;
  min-height: 100vh;
  top: 0;
  left: 64px;
  width: calc(100% - 64px);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
}
.sidebar.open ~ .home-section {
  left: 240px;
  width: calc(100% - 240px);
}

@media (max-width: 420px) {
  .nav-tooltip {
    display: none;
  }
}
</style>
